import React, { useRef } from "react";
import { createPortal } from "react-dom";

import { useLocalStorage } from "usehooks-ts";

interface CallManagerWrapperProps {
  children: React.ReactNode;
  verticalMargin?: string;
}

const PortalContext =
  React.createContext<React.RefObject<HTMLDivElement> | null>(null);

/**
 * TestPortalProvider - A simplified version of CallManagerWrapper for Storybook
 * This enables the TestPortal component to work in isolated stories.
 */
export const TestPortalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const portalRef = useRef<HTMLDivElement>(null);

  return (
    <PortalContext.Provider value={portalRef}>
      <div className="relative">
        {children}
        <div className="absolute right-0 top-0 w-64">
          <div ref={portalRef} className="w-full" />
        </div>
      </div>
    </PortalContext.Provider>
  );
};

export function CallManagerWrapper({
  children,
}: Readonly<CallManagerWrapperProps>) {
  const [isTesting] = useLocalStorage("testing-mode", false);
  const portalRef = useRef<HTMLDivElement>(null);

  return (
    <PortalContext.Provider value={portalRef}>
      <div
        className={`h-full w-full max-w-none xl:max-w-7xl xl:mx-auto xl:p-4 flex flex-col ${isTesting ? "lg:p-8" : ""}`}
      >
        <div
          className={`flex flex-grow overflow-hidden ${!isTesting ? "justify-center" : ""}`}
        >
          <div
            className={`h-full transition-all duration-300 ease-in-out ${
              isTesting
                ? "w-1/2 border-r-2 border-gray-300"
                : "w-full rounded-md"
            } bg-white`}
          >
            {children}
          </div>
          <div
            className={`h-full transition-all duration-300 ease-in-out ${
              isTesting ? "ml-0 w-1/2" : "ml-0 w-0"
            } overflow-hidden bg-gray-100`}
          >
            <div ref={portalRef} className="h-full w-full" />
          </div>
        </div>
      </div>
    </PortalContext.Provider>
  );
}

export const TestPortal: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const portalRef = React.useContext(PortalContext);
  const [isTesting] = useLocalStorage("testing-mode", false);

  if (!portalRef?.current) return null;

  return createPortal(
    <div className={`${isTesting ? "block" : "hidden"}`}>{children}</div>,
    portalRef.current,
  );
};
